/**
 * Fallback when the API has not returned `publicEntryStakes` yet — keep in sync with backend default
 * (`BULLION_PUBLIC_ENTRY_STAKES` env or 500,1500,5000).
 */
export const BULLION_PUBLIC_ENTRY_STAKES_FALLBACK = [500, 1500, 5000] as const;
export const BULLION_ENTRY_COINS = BULLION_PUBLIC_ENTRY_STAKES_FALLBACK[0];
export const BULLION_GROUP_COUNT = 4;
/** Max regular chips per market group (TEAM / PLAYER / MATCH / INNINGS); total regular = this × four groups. */
export const BULLION_GROUP_CHIP_LIMIT = 10;
/** Standard match-play chips (gold); one full stake per group. */
export const BULLION_REGULAR_CHIPS = BULLION_GROUP_CHIP_LIMIT * BULLION_GROUP_COUNT;
/** Regular chips are the only physical chips; bonuses are placed separately (see `BULLION_BONUS_TIERS`). */
export const BULLION_TOTAL_CHIPS = BULLION_REGULAR_CHIPS;
export const BULLION_CHIPS_PER_CLICK = 1;
/** Scoring units per physical chip at 1.00 odds (`line units = chips × this`, before × odds). */
export const BULLION_PTS_PER_CHIP = 100;
/** League Bullion pts-per-chip (same default as cash; overridable via env on the backend). */
export const LEAGUE_BULLION_PTS_PER_CHIP = 100;

/** Bonus tier identifiers (`small` retained for legacy saved plays). */
export type BullionBonusTier = "small" | "large";

/**
 * One single-use Bullion bonus (450 pts = 4.5 bars at {@link BULLION_PTS_PER_CHIP}). Joins the stake
 * on the line it is placed on, so a winning line pays the bonus at that line's price. Shown as
 * stacked bullion bars in the UI.
 */
export const BULLION_BONUS_TIERS: ReadonlyArray<{ tier: BullionBonusTier; points: number }> = [
    { tier: "large", points: 450 },
];
/** Number of bonuses a card must place before it can be entered. */
export const BULLION_BONUS_COUNT = BULLION_BONUS_TIERS.length;
/** Stake points each bonus tier adds to its line (bar-equivalent × pts per chip). */
export const BULLION_BONUS_POINTS_BY_TIER: Record<BullionBonusTier, number> = {
    small: 150,
    large: 450,
};
/** Total bonus stake points across the tiers a card places. */
export const BULLION_BONUS_TOTAL_POINTS = BULLION_BONUS_TIERS.reduce((sum, b) => sum + b.points, 0);

/**
 * Points that equal one "bonus bar" in UI copy (450 → 4.5). Same scale as regular bars
 * ({@link BULLION_PTS_PER_CHIP}).
 */
export const BULLION_BONUS_BAR_UNIT_PTS = BULLION_PTS_PER_CHIP;

/** Bonus stake on the same scale as placed bars (450 pts → 4.5 bars). */
export function bullionBonusBars(bonusPoints: number): number {
    const pts = Math.max(0, Number(bonusPoints) || 0);
    if (BULLION_BONUS_BAR_UNIT_PTS <= 0) return 0;
    return pts / BULLION_BONUS_BAR_UNIT_PTS;
}

/** Bar-scale label for a bonus — `450` → `"4.5"`, `200` → `"2"`. */
export function formatBullionBonusBars(bonusPoints: number): string {
    const bars = bullionBonusBars(bonusPoints);
    return Number.isInteger(bars) ? String(bars) : bars.toFixed(1);
}
/** Upper bound if every regular bar scored at 1.00 plus the bonus (not a realistic card total). */
export const BULLION_MAX_POTENTIAL_PTS = BULLION_TOTAL_CHIPS * BULLION_PTS_PER_CHIP + BULLION_BONUS_TOTAL_POINTS;

export const BULLION_GROUP_TAB_LABELS = ["TEAM", "PLAYER", "MATCH", "INNINGS"] as const;

/** Upcoming (non-live) fixtures can start up to this many days from now and still appear in the Bullion lobby. */
export const BULLION_LOBBY_UPCOMING_DAYS = 5;
/** Max Bullion lobby cards after merge + filter + sort (IPL-first, then kick-off). */
export const BULLION_LOBBY_MAX_MATCHES = 24;

/**
 * After landing on `/bullion?match=&game=`, defer marking `bullion_match_challenge` read until the Header
 * challenge toast can fetch while still unread (~6.4s dismiss + buffer).
 */
export const BULLION_CHALLENGE_NOTIFICATION_ACK_DELAY_MS = 8000;
